import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Heading,
  Text,
  TextField,
  Toggle,
  Tooltip,
  IconButton,
} from '@vibe/core';
import { Add, Edit, NavigationChevronUp, NavigationChevronDown } from '@vibe/icons';
import {
  getWorklogCategories,
  createWorklogCategory,
  updateWorklogCategory,
  reorderWorklogCategories,
  type WorklogCategory,
  type CreateWorklogCategoryPayload,
} from '../../api/worklog-category.api';

// ─── Inline editor ──────────────────────────────────────────────────────────

interface CategoryFormState {
  name: string;
  isBillable: boolean;
  color: string;
}

const DEFAULT_FORM: CategoryFormState = {
  name: '',
  isBillable: false,
  color: '#808080',
};

const PRESET_COLORS = [
  '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#94a3b8', '#f97316', '#06b6d4',
];

// ─── Component ──────────────────────────────────────────────────────────────

const WorklogCategoriesPanel: React.FC = () => {
  const [categories, setCategories] = useState<WorklogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CategoryFormState>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getWorklogCategories(true);
      setCategories(data);
    } catch {
      setError('Failed to load worklog categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleAdd = useCallback(() => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setShowForm(true);
  }, []);

  const handleEdit = useCallback((cat: WorklogCategory) => {
    setEditingId(cat.id);
    setForm({ name: cat.name, isBillable: cat.isBillable, color: cat.color });
    setShowForm(true);
  }, []);

  const handleCancel = useCallback(() => {
    setShowForm(false);
    setEditingId(null);
    setForm(DEFAULT_FORM);
  }, []);

  const handleSave = useCallback(async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await updateWorklogCategory(editingId, {
          name: form.name.trim(),
          isBillable: form.isBillable,
          color: form.color,
        });
      } else {
        const payload: CreateWorklogCategoryPayload = {
          name: form.name.trim(),
          isBillable: form.isBillable,
          color: form.color,
        };
        await createWorklogCategory(payload);
      }
      setShowForm(false);
      setEditingId(null);
      setForm(DEFAULT_FORM);
      await fetchCategories();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [editingId, form, fetchCategories]);

  const handleToggleActive = useCallback(async (cat: WorklogCategory) => {
    try {
      await updateWorklogCategory(cat.id, { isActive: !cat.isActive });
      await fetchCategories();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update';
      setError(msg);
    }
  }, [fetchCategories]);

  const handleSetDefault = useCallback(async (cat: WorklogCategory) => {
    try {
      await updateWorklogCategory(cat.id, { isDefault: true });
      await fetchCategories();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to set default';
      setError(msg);
    }
  }, [fetchCategories]);

  const handleMoveUp = useCallback(async (index: number) => {
    if (index === 0) return;
    const ordered = [...categories];
    [ordered[index - 1], ordered[index]] = [ordered[index], ordered[index - 1]];
    const orderedIds = ordered.map((c) => c.id);
    try {
      const updated = await reorderWorklogCategories(orderedIds);
      setCategories(updated);
    } catch {
      setError('Failed to reorder');
    }
  }, [categories]);

  const handleMoveDown = useCallback(async (index: number) => {
    if (index >= categories.length - 1) return;
    const ordered = [...categories];
    [ordered[index], ordered[index + 1]] = [ordered[index + 1], ordered[index]];
    const orderedIds = ordered.map((c) => c.id);
    try {
      const updated = await reorderWorklogCategories(orderedIds);
      setCategories(updated);
    } catch {
      setError('Failed to reorder');
    }
  }, [categories]);

  // ─── Render ─────────────────────────────────────────────────────────────

  if (loading) {
    return <Text>Loading worklog categories...</Text>;
  }

  return (
    <div className="rp-worklog-categories">
      {error && (
        <div className="rp-worklog-categories__error">
          <Text style={{ color: 'var(--negative-color, #d83a52)' }}>{error}</Text>
        </div>
      )}

      <div className="rp-worklog-categories__header">
        <Text type={Text.types.TEXT2} style={{ color: 'var(--secondary-text-color, #676879)', whiteSpace: 'normal', overflow: 'visible' }}>
          Configure how time entries are categorized. Categories marked as &quot;Billable&quot; are aggregated in reports for invoicing. Keep only 1 active category to hide the category selector from the log time dialog.
        </Text>
        <Button
          size={Button.sizes.SMALL}
          leftIcon={Add}
          onClick={handleAdd}
        >
          Add Category
        </Button>
      </div>

      {/* Inline form for add/edit */}
      {showForm && (
        <div className="rp-worklog-categories__form">
          <Heading type={Heading.types.H3}>
            {editingId ? 'Edit Category' : 'New Category'}
          </Heading>
          <div className="rp-worklog-categories__form-row">
            <TextField
              placeholder="Category name"
              value={form.name}
              onChange={(val: string) => setForm((prev) => ({ ...prev, name: val }))}
              size={TextField.sizes.SMALL}
            />
          </div>
          <div className="rp-worklog-categories__form-row">
            <label className="rp-worklog-categories__form-label">
              <Toggle
                isSelected={form.isBillable}
                onChange={() => setForm((prev) => ({ ...prev, isBillable: !prev.isBillable }))}
                size="small"
              />
              <span>Billable</span>
            </label>
          </div>
          <div className="rp-worklog-categories__form-row">
            <Text type={Text.types.TEXT2}>Color</Text>
            <div className="rp-worklog-categories__color-picker">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  className={`rp-worklog-categories__color-swatch ${form.color === c ? 'rp-worklog-categories__color-swatch--selected' : ''}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setForm((prev) => ({ ...prev, color: c }))}
                  title={c}
                />
              ))}
            </div>
          </div>
          <div className="rp-worklog-categories__form-actions">
            <Button size={Button.sizes.SMALL} onClick={handleSave} loading={saving}>
              {editingId ? 'Update' : 'Create'}
            </Button>
            <Button size={Button.sizes.SMALL} kind={Button.kinds.TERTIARY} onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Category list */}
      <div className="rp-worklog-categories__list">
        {categories.map((cat, index) => (
          <div
            key={cat.id}
            className={`rp-worklog-categories__item ${!cat.isActive ? 'rp-worklog-categories__item--inactive' : ''}`}
          >
            <span
              className="rp-worklog-categories__color-dot"
              style={{ backgroundColor: cat.color }}
            />
            <div className="rp-worklog-categories__item-info">
              <Text weight="bold">{cat.name}</Text>
              <div className="rp-worklog-categories__badges">
                {cat.isBillable && (
                  <span className="rp-worklog-categories__badge rp-worklog-categories__badge--billable">
                    Billable
                  </span>
                )}
                {cat.isDefault && (
                  <span className="rp-worklog-categories__badge rp-worklog-categories__badge--default">
                    Default
                  </span>
                )}
                {!cat.isActive && (
                  <span className="rp-worklog-categories__badge rp-worklog-categories__badge--inactive">
                    Inactive
                  </span>
                )}
              </div>
            </div>
            <div className="rp-worklog-categories__item-actions">
              {!cat.isDefault && cat.isActive && (
                <Tooltip content="Set as default">
                  <Button
                    size={Button.sizes.SMALL}
                    kind={Button.kinds.TERTIARY}
                    onClick={() => handleSetDefault(cat)}
                  >
                    Set Default
                  </Button>
                </Tooltip>
              )}
              <IconButton
                icon={Edit}
                size={IconButton.sizes.SMALL}
                kind={IconButton.kinds.TERTIARY}
                ariaLabel="Edit"
                onClick={() => handleEdit(cat)}
              />
              <IconButton
                icon={NavigationChevronUp}
                size={IconButton.sizes.SMALL}
                kind={IconButton.kinds.TERTIARY}
                ariaLabel="Move up"
                disabled={index === 0}
                onClick={() => handleMoveUp(index)}
              />
              <IconButton
                icon={NavigationChevronDown}
                size={IconButton.sizes.SMALL}
                kind={IconButton.kinds.TERTIARY}
                ariaLabel="Move down"
                disabled={index === categories.length - 1}
                onClick={() => handleMoveDown(index)}
              />
              <Toggle
                isSelected={cat.isActive}
                onChange={() => handleToggleActive(cat)}
                size="small"
                ariaLabel={cat.isActive ? 'Deactivate' : 'Activate'}
              />
            </div>
          </div>
        ))}
        {categories.length === 0 && (
          <Text type={Text.types.TEXT2} style={{ color: 'var(--secondary-text-color, #676879)', padding: 16 }}>
            No categories configured yet. Click &quot;Add Category&quot; to create one, or categories will be seeded automatically when worklogs are created.
          </Text>
        )}
      </div>
    </div>
  );
};

export default WorklogCategoriesPanel;
